import { useAppSelector } from "@/hooks/useAppSelector";
import { useNPVSettingsUpsert } from "@/hooks/useNPVSettingsUpsert";
import { useUniquePossibilities } from "@/hooks/useUniquePossibilities";
import { useUniquePossibilitiesUpsert } from "@/hooks/useUniquePossibilitiesUpsert";
import npvSlice, { npvActions } from "@/redux/npv/npv-slice";
import { uqActions } from "@/redux/uq/uq-slice";
import CustomButton from "@components/CustomButton";
import { Popup } from "devextreme-react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import ReactRouterPrompt from "react-router-prompt";

export default function NavigationBlocker() {
  const { id } = useParams();
  const {
    isModified: isUpModified,
    gridSet,
    settings,
    modifiedOn,
    headerTitles: titles,
  } = useAppSelector((state) => state.uq);
  const { isModified: isNpvModified, settings: npvSettings } = useAppSelector((state) => state.npv);
  const { mutate: upsertUq } = useUniquePossibilitiesUpsert();
  const { data: upData } = useUniquePossibilities(id!);
  const { mutate: saveNPVSettings } = useNPVSettingsUpsert();
  const dispatch = useDispatch();

  const household = upData?.household;

  const saveUQ = () => {
    upsertUq({
      data: {
        modifiedOn: modifiedOn,
        modifiedBy: upData?.modifiedBy,
        household: {
          externalCode: id as string,
        },
        data: JSON.stringify({
          gridSet,
          settings,
          household,
          titles,
        }),
      },
      overwrite: true,
    });
  };

  const blockerFn = () => {
    if (isUpModified || isNpvModified) return true;
    return false;
  };

  return (
    <ReactRouterPrompt when={blockerFn}>
      {({ isActive, onConfirm, onCancel }) => (
        <Popup
          width={450}
          height={"fit"}
          showTitle={false}
          visible={isActive}
          showCloseButton={false}
          className="shadow-light"
        >
          {isUpModified ? (
            <div className="flex flex-col gap-5">
              <span className="block text-center text-lg font-medium">
                Do you want to save changes?
              </span>
              <div className="flex justify-center gap-4">
                <CustomButton
                  text="Cancel"
                  onClickHandler={() => {
                    onCancel();
                  }}
                  className="w-[7.625rem] !px-2"
                />
                <CustomButton
                  text="Don't save"
                  onClickHandler={() => {
                    onConfirm();
                    dispatch(uqActions.setIsModified(false));
                  }}
                  className="w-[7.625rem] !px-2"
                />
                <CustomButton
                  variant="primary"
                  onClickHandler={() => {
                    saveUQ();
                    dispatch(uqActions.setIsModified(false));

                    onConfirm();
                  }}
                  text="Save"
                  className="w-[7.625rem] !px-2"
                />
              </div>
            </div>
          ) : null}

          {isNpvModified ? (
            <div className="flex flex-col gap-5">
              <span className="block text-center text-lg font-medium">
                Do you want to save changes?
              </span>
              <div className="flex justify-center gap-4">
                <CustomButton
                  text="Cancel"
                  onClickHandler={() => {
                    onCancel();
                  }}
                  className="w-[7.625rem] !px-2"
                />
                <CustomButton
                  text="Don't save"
                  className="w-[7.625rem] !px-2"
                  onClickHandler={() => {
                    if (upData?.npvSettings) {
                      dispatch(
                        npvActions.setSettings(structuredClone(upData?.npvSettings ?? npvSettings))
                      );
                    } else {
                      dispatch(
                        npvActions.setSettings({
                          irr: npvSlice.getInitialState().settings.irr,
                          npvAge: npvSlice.getInitialState().settings.npvAge,
                          yearsToRetire: npvSlice.getInitialState().settings.yearsToRetire,
                        })
                      );
                    }
                    setTimeout(() => {
                      dispatch(npvActions.setIsModified(false));
                    }, 200);
                    onConfirm();
                  }}
                />
                <CustomButton
                  variant="primary"
                  className="w-[7.625rem] !px-2"
                  onClickHandler={() => {
                    saveNPVSettings({
                      data: JSON.stringify({
                        yearsToRetire: npvSettings.yearsToRetire,
                        npvAge: npvSettings.npvAge,
                        irr: npvSettings.irr,
                      }),
                      householdId: upData?.household?.externalCode,
                    });
                    setTimeout(() => {
                      dispatch(npvActions.setIsModified(false));
                    }, 200);
                    onConfirm();
                  }}
                  text="Save"
                />
              </div>
            </div>
          ) : null}
        </Popup>
      )}
    </ReactRouterPrompt>
  );
}
